/**
 * @fileoverview Error boundary component for catching and displaying React errors.
 *
 * Provides graceful error handling with user-friendly fallback UI,
 * different messages for network vs application errors, and error logging.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Props for the ErrorBoundary component.
 */
export interface ErrorBoundaryProps {
  /** Child components to render when no error */
  children: ReactNode;
  /** Optional custom fallback component */
  fallback?: ReactNode;
}

/**
 * State for the ErrorBoundary component.
 */
interface ErrorBoundaryState {
  /** Whether an error has occurred */
  hasError: boolean;
  /** The caught error object */
  error: Error | null;
  /** Whether the error is a network-related error */
  isNetworkError: boolean;
}

/**
 * Checks if an error is network-related.
 *
 * @param error - The error to check
 * @returns True if the error appears to be network-related
 */
function isNetworkError(error: Error): boolean {
  const networkErrorPatterns = [
    'NetworkError',
    'Failed to fetch',
    'Network request failed',
    'net::ERR_',
    'ECONNREFUSED',
    'ETIMEDOUT',
    'ENOTFOUND',
    'fetch failed',
    'Load failed',
    'Connection refused',
    'Unable to connect',
  ];

  const errorMessage = error.message.toLowerCase();
  const errorName = error.name.toLowerCase();

  return networkErrorPatterns.some(
    (pattern) =>
      errorMessage.includes(pattern.toLowerCase()) ||
      errorName.includes(pattern.toLowerCase())
  );
}

/**
 * Error boundary component that catches JavaScript errors in child components.
 *
 * Renders a fallback UI when an error is caught, with different messages
 * for network errors versus application errors. Includes a reload button
 * and logs errors to the console with full stack traces.
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  /**
   * Creates an instance of ErrorBoundary.
   *
   * @param props - The component props
   */
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isNetworkError: false,
    };
  }

  /**
   * Static lifecycle method called when an error is thrown in a descendant component.
   * Updates state to trigger fallback UI rendering.
   *
   * @param error - The thrown error
   * @returns Updated state with error information
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      isNetworkError: isNetworkError(error),
    };
  }

  /**
   * Lifecycle method called after an error has been caught.
   * Logs the error with full stack trace to the console.
   *
   * @param error - The thrown error
   * @param errorInfo - React error info including component stack
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error with full details for debugging
    console.error('ErrorBoundary caught an error:');
    console.error('Error:', error);
    console.error('Error message:', error.message);
    console.error('Error name:', error.name);
    console.error('Stack trace:', error.stack);
    console.error('Component stack:', errorInfo.componentStack);

    // Log whether it was detected as a network error
    if (this.state.isNetworkError) {
      console.error('Error type: Network error');
    } else {
      console.error('Error type: Application error');
    }
  }

  /**
   * Handles the reload button click by refreshing the page.
   */
  handleReload = (): void => {
    window.location.reload();
  };

  /**
   * Handles going back to the previous page.
   */
  handleGoBack = (): void => {
    window.history.back();
  };

  /**
   * Resets the error state to try rendering children again.
   */
  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      isNetworkError: false,
    });
  };

  /**
   * Renders the component or fallback UI if an error has occurred.
   *
   * @returns The children or fallback UI
   */
  render(): ReactNode {
    const { hasError, error, isNetworkError: isNetwork } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback;
      }

      // Render the default fallback UI
      return (
        <div className="flex min-h-screen items-center justify-center bg-slate-900 p-4">
          <div className="max-w-md text-center">
            {/* Error icon */}
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20">
              {isNetwork ? (
                // Network error icon (WiFi off)
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
                  />
                </svg>
              ) : (
                // Application error icon (exclamation)
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              )}
            </div>

            {/* Error title */}
            <h1 className="mb-2 text-2xl font-bold text-white">
              {isNetwork ? 'Connection Problem' : 'Something Went Wrong'}
            </h1>

            {/* Error description */}
            <p className="mb-6 text-slate-400">
              {isNetwork
                ? "We couldn't connect to the server. Please check your internet connection and try again."
                : 'An unexpected error occurred in the application. We apologize for the inconvenience.'}
            </p>

            {/* Error details (collapsed by default for app errors) */}
            {error && !isNetwork && (
              <details className="mb-6 text-left">
                <summary className="cursor-pointer text-sm text-slate-500 hover:text-slate-400">
                  View error details
                </summary>
                <div className="mt-2 rounded-lg bg-slate-800 p-3">
                  <p className="font-mono text-xs text-red-400">
                    {error.name}: {error.message}
                  </p>
                  {error.stack && (
                    <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap font-mono text-xs text-slate-500">
                      {error.stack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            {/* Action buttons */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              {/* Retry button for network errors */}
              {isNetwork && (
                <button
                  onClick={this.handleRetry}
                  className="rounded-lg bg-blue-600 px-6 py-2 font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Try Again
                </button>
              )}

              {/* Reload button */}
              <button
                onClick={this.handleReload}
                className={`rounded-lg px-6 py-2 font-medium transition-colors ${
                  isNetwork
                    ? 'bg-slate-700 text-white hover:bg-slate-600'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Reload Page
              </button>

              {/* Go back button */}
              <button
                onClick={this.handleGoBack}
                className="rounded-lg bg-slate-700 px-6 py-2 font-medium text-white transition-colors hover:bg-slate-600"
              >
                Go Back
              </button>
            </div>

            {/* Support hint */}
            <p className="mt-6 text-xs text-slate-600">
              If this problem persists, please contact support.
            </p>
          </div>
        </div>
      );
    }

    return children;
  }
}
